import { useCallback, useRef, useState } from 'react';
import { datadogRum } from '@datadog/browser-rum';
import { resumeWorkflowApi } from 'api/profile';
import {
    getFallbackType,
    hasFallbacksRemaining,
    isResumeWorkflowFallbackError,
} from 'lib/ssnFallback';

/**
 * Orchestrates the resume-workflow (Auth DB) SSN fallback. Encapsulates the
 * call machinery, the SSN-fallback retry state, and the duplicate-call guard.
 *
 * Behavior is lifted verbatim from VerificationOptions Call Site 4 — see
 * refactor plans/ssn-fallback-plan.md §9 (Sacred Preservation Checklist).
 * The decision tree (verification-completion / mismatch / attempts-exhausted)
 * stays in the host component.
 *
 * @param {object}   opts
 * @param {string}   opts.userAccessCode    - user UUID (uac)
 * @param {object}   opts.ssnConfig         - profile.company.ssnConfig
 * @param {object}   opts.company           - { uuid, name } for Datadog context
 * @param {Function} opts.onFallbackNeeded  - host shows the SSN modal (e.g. setShow(true))
 * @param {Function} opts.onSuccess         - host redirect on resume-workflow success
 * @param {Function} opts.setIsFetching     - host loading flag setter
 * @returns {{ askForFallback: (string|null), authDbVerificationRef: object, runResumeWorkflow: Function }}
 */
export function useSsnFallback({
    userAccessCode,
    ssnConfig,
    company,
    onFallbackNeeded,
    onSuccess,
    setIsFetching,
}) {
    const [askForFallback, setAskForFallback] = useState(null);
    const authDbVerificationRef = useRef(false);

    const runResumeWorkflow = useCallback(
        async (currentFallback) => {
            const fallback = currentFallback ?? ssnConfig?.remainingFallbacks;

            try {
                setIsFetching(true);

                const resumeWorkflowApiResponse = await resumeWorkflowApi(userAccessCode);

                // resume-workflow needs more info (SSN) — show modal and allow retry
                if (
                    isResumeWorkflowFallbackError(resumeWorkflowApiResponse?.data?.status?.error) &&
                    hasFallbacksRemaining(fallback)
                ) {
                    onFallbackNeeded();
                    setAskForFallback(getFallbackType(fallback));
                    authDbVerificationRef.current = false;
                    return;
                }

                // no errors from resume-workflow — redirect user to the next route.
                // Keep authDbVerificationRef.current = true (guard stays closed) so the
                // decision useEffect can't re-fire runResumeWorkflow → repeated history.push.
                onSuccess();
            } catch (error) {
                datadogRum.addError(error, {
                    userUUID: userAccessCode,
                    companyUUID: company?.uuid,
                    companyName: company?.name,
                });
            } finally {
                setIsFetching(false);
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [
            userAccessCode,
            company?.uuid,
            company?.name,
            ssnConfig,
            onFallbackNeeded,
            onSuccess,
            setIsFetching,
        ]
    );

    return { askForFallback, authDbVerificationRef, runResumeWorkflow };
}
